<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <div class="tw-col-span-12 lg:tw-col-span-12">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 v-if="!loading" class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Contacts</h1>
                        <div v-else class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-1 tw-animate-pulse"></div>
                        
                        <p v-if="!loading" class="tw-text-sm tw-text-gray-600">{{ totalContacts || contacts.length }} contacts total</p>
                        <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                    </div>
                    
                    <!-- Header Actions -->
                    <div v-if="!loading" class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search contacts..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'grid' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:widget-4-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900'
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Export Button -->
                        <a-button v-if="false"
                            @click="exportContacts"
                            intent="secondary"
                            left-icon="solar:export-bold-duotone"
                        >
                            Export
                        </a-button>
                        
                    </div>
                    
                    <!-- Header Actions Skeleton -->
                    <div v-else class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-64 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-20 tw-animate-pulse"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded-md tw-w-28 tw-animate-pulse"></div>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Online Filter -->
                    <FormField
                        v-model="selectedStatus"
                        type="select"
                        placeholder="All contacts"
                        left-icon="solar:user-check-bold-duotone"
                        hide-label
                        hide-errors
                        class="tw-min-w-[150px]"
                    >
                        <option value="">All Contacts</option>
                        <option value="active">Online Only</option>
                    </FormField>

                    <!-- Sort -->
                    <FormField
                        v-model="sortBy"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:sort-horizontal-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="company">Sort by Company</option>
                        <option value="status">Sort by Status</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>

                    <!-- Bulk Actions -->
                    <div v-if="selectedContacts.length > 0" class="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                        <span class="tw-text-sm tw-text-gray-600">{{ selectedContacts.length }} selected</span>
                        <a-button
                            @click="exportSelected"
                            intent="secondary"
                            left-icon="solar:export-bold-duotone"
                            class="tw-text-sm"
                        >
                            Export
                        </a-button>
                        <a-button
                            @click="messageSelected"
                            intent="primary"
                            left-icon="solar:chat-round-bold-duotone"
                            class="tw-text-sm"
                        >
                            Message
                        </a-button>
                        <a-button
                            @click="removeSelected"
                            intent="danger"
                            left-icon="solar:trash-bin-2-bold-duotone"
                            class="tw-text-sm"
                        >
                            Remove
                        </a-button>
                    </div>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Grid View Skeleton -->
                        <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                            <div
                                v-for="n in 8"
                                :key="n"
                                class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-animate-pulse"
                            >
                                <!-- Skeleton Checkbox and Actions -->
                                <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                    <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                    </div>
                                </div>

                                <!-- Skeleton Avatar -->
                                <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-full tw-mx-auto tw-mb-3"></div>

                                <!-- Skeleton Contact Info -->
                                <div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4 tw-mx-auto"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-full"></div>
                                    <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16 tw-mx-auto tw-mt-2"></div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-px-4 tw-py-3 tw-text-left">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Contact</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Company</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Email</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr
                                        v-for="n in 12"
                                        :key="n"
                                        class="tw-animate-pulse"
                                    >
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-items-center">
                                                <div class="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded-full tw-mr-3"></div>
                                                <div>
                                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-1"></div>
                                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-40"></div>
                                        </td>

                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="filteredContacts.length === 0 && !searchQuery && !selectedStatus" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:users-group-two-rounded-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No contacts yet</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start connecting with other users and they'll appear here</p>
                        <a-button
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Find People
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="filteredContacts.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:magnifer-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No contacts found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Grid View -->
                    <div v-else-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                        <div
                            v-for="contact in paginatedContacts"
                            :key="contact.id"
                            class="tw-group tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer"
                            @click="selectContact(contact)"
                        >
                            <!-- Selection Checkbox -->
                            <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                <input
                                    type="checkbox"
                                    :checked="selectedContacts.includes(contact.id)"
                                    @click.stop="toggleContactSelection(contact.id)"
                                    class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                />
                            </div>

                            <!-- Avatar -->
                            <div class="tw-flex tw-justify-center tw-mb-3">
                                <div class="tw-relative">
                                    <img
                                        v-if="contact.profile_image || contact.avatar"
                                        :src="contact.profile_image || contact.avatar"
                                        :alt="contact.name"
                                        class="tw-w-16 tw-h-16 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-100"
                                    />
                                    <div
                                        v-else
                                        class="tw-w-16 tw-h-16 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-gray-100"
                                    >
                                        <icon icon="solar:user-bold-duotone" class="tw-text-2xl tw-text-primary-500" />
                                    </div>
                                    
                                    <!-- Online Status Indicator -->
                                    <div
                                        v-if="contact.is_online || contact.status === 'active'"
                                        class="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-rounded-full tw-border-2 tw-border-white tw-bg-green-500"
                                    ></div>
                                </div>
                            </div>

                            <!-- Contact Info -->
                            <div class="tw-text-center">
                                <h3 class="tw-font-semibold tw-text-gray-900 tw-mb-1 tw-truncate" :title="contact.name">
                                    {{ contact.name }}
                                </h3>
                                <p v-if="contact.role" class="tw-text-sm tw-text-gray-600 tw-mb-1 tw-truncate" :title="contact.role">
                                    {{ contact.role }}
                                </p>
                                <p v-if="contact.company_name || contact.company?.name" class="tw-text-sm tw-text-gray-500 tw-mb-2 tw-truncate tw-flex tw-items-center tw-justify-center tw-gap-1.5" :title="contact.company_name || contact.company?.name">
                                    <img 
                                        v-if="contact.company_logo"
                                        :src="contact.company_logo"
                                        :alt="contact.company_name || contact.company?.name"
                                        class="tw-w-4 tw-h-4 tw-rounded tw-object-cover"
                                    />
                                    <icon v-else icon="solar:buildings-bold-duotone" class="tw-text-base" />
                                    {{ contact.company_name || contact.company?.name }}
                                </p>
                                
                
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-px-4 tw-py-3 tw-text-left">
                                        <input
                                            type="checkbox"
                                            :checked="allContactsSelected"
                                            @change="toggleAllContacts"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Contact</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Company</th>
                                    <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Email</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="contact in paginatedContacts"
                                    :key="contact.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-4 tw-py-4">
                                        <input
                                            type="checkbox"
                                            :checked="selectedContacts.includes(contact.id)"
                                            @change="toggleContactSelection(contact.id)"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </td>
                                    <td class="tw-px-4 tw-py-4">
                                        <div class="tw-flex tw-items-center">
                                            <div class="tw-relative tw-mr-3">
                                                <img
                                                    v-if="contact.profile_image || contact.avatar"
                                                    :src="contact.profile_image || contact.avatar"
                                                    :alt="contact.name"
                                                    class="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
                                                />
                                                <div
                                                    v-else
                                                    class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center"
                                                >
                                                    <icon icon="solar:user-bold-duotone" class="tw-text-lg tw-text-primary-500" />
                                                </div>
                                                
                                                <!-- Online Status Indicator -->
                                                <div
                                                    v-if="contact.is_online || contact.status === 'active'"
                                                    class="tw-absolute tw-bottom-0 tw-right-0 tw-w-3 tw-h-3 tw-rounded-full tw-border tw-border-white tw-bg-green-500"
                                                ></div>
                                            </div>
                                            <div>
                                                <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ contact.name }}</div>
                                                <div v-if="contact.role" class="tw-text-sm tw-text-gray-500">{{ contact.role }}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-900">
                                        <div v-if="contact.company_name || contact.company?.name" class="tw-flex tw-items-center tw-gap-1.5">
                                            <img 
                                                v-if="contact.company_logo"
                                                :src="contact.company_logo"
                                                :alt="contact.company_name || contact.company?.name"
                                                class="tw-w-4 tw-h-4 tw-rounded tw-object-cover"
                                            />
                                            <icon v-else icon="solar:buildings-bold-duotone" class="tw-text-base tw-text-gray-500" />
                                            {{ contact.company_name || contact.company?.name }}
                                        </div>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-500">{{ contact.email || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            <span v-if="pagination.value?.total">
                                Showing {{ ((pagination.value.current_page || currentPage) - 1) * itemsPerPage + 1 }} to {{ Math.min((pagination.value.current_page || currentPage) * itemsPerPage, pagination.value.total) }} of {{ pagination.value.total }} contacts
                            </span>
                            <span v-else>
                                Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, filteredContacts.length) }} of {{ filteredContacts.length }} contacts
                            </span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="loadData(currentPage - 1)"
                                :disabled="currentPage === 1 || (pagination.value?.current_page === 1)"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="loadData(currentPage + 1)"
                                :disabled="currentPage === totalPages || (pagination.value?.current_page === pagination.value?.last_page)"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingContact"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            :title="deleteModalTitle"
            :description="deleteModalDescription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import FormField from '@/components/form/FormField.vue'
import useAlert from '@/composables/useAlert'
import myCompanyApi from '@/api/my-company'

const router = useRouter()

// Data
const loading = ref(false)
const searchQuery = ref('')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const selectedContacts = ref([])
const selectedStatus = ref('')
const deleteModalVisible = ref(false)
const deletingContact = ref(false)
const contactToDelete = ref(null)

// Sort and filters
const sortBy = ref('name')

// Contacts data from API
const contacts = ref([])
const totalContacts = ref(0)
const pagination = ref({})

// Computed properties
const hasActiveFilters = computed(() => {
    return selectedStatus.value || searchQuery.value
})

const filteredContacts = computed(() => {
    let filtered = [...contacts.value]

    // Status filter (client-side if not handled by API)
    if (selectedStatus.value) {
        // You can add status filtering logic here if needed
        // For now, all contacts pass through
        // filtered = filtered.filter(contact => contact.status === selectedStatus.value)
    }

    // Sort (client-side)
    filtered.sort((a, b) => {
        switch (sortBy.value) {
            case 'name':
                return (a.name || '').localeCompare(b.name || '')
            case 'company': {
                const companyA = a.company?.name || ''
                const companyB = b.company?.name || ''
                return companyA.localeCompare(companyB)
            }
            case 'connected': {
                const dateA = a.connectedDate ? new Date(a.connectedDate) : new Date(0)
                const dateB = b.connectedDate ? new Date(b.connectedDate) : new Date(0)
                return dateB - dateA
            }
            case 'interaction': {
                // Not available in current API response
                return 0
            }
            default:
                return 0
        }
    })

    return filtered
})

const totalPages = computed(() => {
    // Use API pagination if available, otherwise calculate from filtered results
    if (pagination.value.last_page) {
        return pagination.value.last_page
    }
    return Math.ceil(filteredContacts.value.length / itemsPerPage.value)
})

const paginatedContacts = computed(() => {
    // If API handles pagination, return all contacts (already paginated)
    if (pagination.value.current_page) {
        return filteredContacts.value
    }
    // Otherwise, paginate client-side
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return filteredContacts.value.slice(start, end)
})

const allContactsSelected = computed(() => {
    return paginatedContacts.value.length > 0 && 
           paginatedContacts.value.every(contact => selectedContacts.value.includes(contact.id))
})

const deleteModalTitle = computed(() => {
    if (selectedContacts.value.length > 1) {
        return `Remove ${selectedContacts.value.length} contacts?`
    }
    return 'Remove contact?'
})

const deleteModalDescription = computed(() => {
    if (selectedContacts.value.length > 1) {
        return 'This action cannot be undone. All selected contacts will be removed from your contact list.'
    }
    return 'This action cannot be undone. The contact will be removed from your contact list.'
})

// Methods
const clearFilters = () => {
    selectedStatus.value = ''
    searchQuery.value = ''
    currentPage.value = 1
}

const toggleContactSelection = (contactId) => {
    const index = selectedContacts.value.indexOf(contactId)
    if (index > -1) {
        selectedContacts.value.splice(index, 1)
    } else {
        selectedContacts.value.push(contactId)
    }
}

const toggleAllContacts = () => {
    if (allContactsSelected.value) {
        selectedContacts.value = selectedContacts.value.filter(id =>
            !paginatedContacts.value.some(contact => contact.id === id)
        )
    } else {
        paginatedContacts.value.forEach(contact => {
            if (!selectedContacts.value.includes(contact.id)) {
                selectedContacts.value.push(contact.id)
            }
        })
    }
}

const selectContact = (contact) => {
    viewProfile(contact)
}

const viewProfile = (contact) => {
    if (contact.id) {
        router.push({
            name: 'shared.community-member',
            params: { id: contact.id }
        })
    }
}

const exportContacts = () => {
    console.log('Exporting all contacts')
    useAlert('Contact export started. You will receive a download link shortly.', 'success')
}

const exportSelected = () => {
    console.log('Exporting selected contacts:', selectedContacts.value)
    useAlert(`Exporting ${selectedContacts.value.length} selected contacts`, 'success')
}

const messageSelected = () => {
    console.log('Sending message to selected contacts:', selectedContacts.value)
    useAlert(`Opening message dialog for ${selectedContacts.value.length} contacts`, 'info')
}

const removeSelected = () => {
    deleteModalVisible.value = true
}

const confirmDelete = () => {
    deletingContact.value = true
    
    // Simulate delete operation
    setTimeout(() => {
        contacts.value = contacts.value.filter(contact => 
            !selectedContacts.value.includes(contact.id)
        )
        selectedContacts.value = []
        deletingContact.value = false
        deleteModalVisible.value = false
        contactToDelete.value = null
        useAlert('Contact(s) removed successfully', 'success')
    }, 1000)
}



const loadData = async (page = 1) => {
    loading.value = true

    try {
        const params = {
            page: page || currentPage.value,
            per_page: itemsPerPage.value
        }

        // Add search query if provided
        if (searchQuery.value && searchQuery.value.trim()) {
            params.search = searchQuery.value.trim()
        }

        // Add status filter if provided
        if (selectedStatus.value) {
            params.status = selectedStatus.value
        }

        const response = await myCompanyApi.getContacts(params)
        const responseData = response.data

        // Handle the API response structure: { code, data: { contacts: [], total: number } }
        if (responseData?.data?.contacts && Array.isArray(responseData.data.contacts)) {
            // Map contacts from API response
            contacts.value = responseData.data.contacts.map(contact => {
                // Get the first connection's company user info if available
                const firstConnection = contact.connections?.[0]
                const companyUser = firstConnection?.company_user
                
                return {
                    ...contact,
                    // Use contact's own data
                    id: contact.id,
                    name: contact.name,
                    email: contact.email,
                    profile_image: contact.profile_image,
                    role: contact.role,
                    // Use company_name and company_logo from contact, fallback to nested structure
                    company_name: contact.company_name || companyUser?.name || contact.company?.name,
                    company_logo: contact.company_logo || contact.company?.logo,
                    company: companyUser ? {
                        name: companyUser.name,
                        id: companyUser.id
                    } : (contact.company_name ? {
                        name: contact.company_name,
                        logo: contact.company_logo
                    } : null),
                    // Get connected date from first connection
                    connectedDate: firstConnection?.connected_at ? new Date(firstConnection.connected_at) : null,
                    // Store connections array
                    connections: contact.connections || []
                }
            })
            
            // Update total count
            totalContacts.value = responseData.data.total || contacts.value.length
            pagination.value = {}
        } else if (responseData?.data && Array.isArray(responseData.data)) {
            // Fallback: if data is directly an array
            contacts.value = responseData.data.map(contact => ({
                ...contact,
                id: contact.id,
                name: contact.name,
                email: contact.email,
                profile_image: contact.profile_image,
                role: contact.role
            }))
            totalContacts.value = responseData.total || contacts.value.length
            pagination.value = {}
        } else {
            contacts.value = []
            totalContacts.value = 0
            pagination.value = {}
        }
    } catch (error) {
        console.error('Error loading contacts:', error)
        useAlert('Failed to load contacts', 'danger')
        contacts.value = []
        totalContacts.value = 0
    } finally {
        loading.value = false
    }
}

// Watch for search query changes (debounced)
let searchTimeout = null
watch(searchQuery, () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        currentPage.value = 1
        loadData(1)
    }, 500)
})

// Watch for status filter changes
watch(selectedStatus, () => {
    currentPage.value = 1
    loadData(1)
})

// Initialize
onMounted(() => {
    loadData(1)
})
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>